import Konva from 'konva';

/**
 * Quiz Konva Renderer
 * 將題目圖片渲染到 Konva 畫布
 */
export class QuizKonvaRenderer {
  /**
   * @param {Konva.Stage} stage - Konva Stage 實例
   * @param {Konva.Layer} layer - Konva Layer 實例
   * @param {Array} saveArr - saveArr 引用（用於保存每頁狀態）
   */
  constructor(stage, layer, saveArr) {
    this.stage = stage;
    this.layer = layer;
    this.saveArr = saveArr;
    this.cancelled = false; // 取消標記
  }

  /**
   * 取消渲染操作
   */
  cancel() {
    this.cancelled = true;
    console.log('🛑 QuizKonvaRenderer 已取消');
  }

  /**
   * 渲染單個題目到 Konva
   * @param {String} imageDataURL - Base64 圖片數據
   * @param {Number} pageIndex - 頁碼索引
   * @returns {Promise<Number>} 返回 pageIndex
   */
  async renderSingleQuestion(imageDataURL, pageIndex) {
    return new Promise((resolve, reject) => {
      // 驗證 dataURL
      if (!imageDataURL || typeof imageDataURL !== 'string') {
        reject(new Error(`無效的圖片數據: pageIndex ${pageIndex}`));
        return;
      }

      // 檢查 dataURL 格式
      if (!imageDataURL.startsWith('data:image/')) {
        console.error(`pageIndex ${pageIndex} 的 dataURL 格式錯誤:`, imageDataURL.substring(0, 100));
        reject(new Error(`圖片格式錯誤: pageIndex ${pageIndex}`));
        return;
      }

      const imageObj = new Image();

      imageObj.onload = () => {
        try {
          console.log(`✅ 圖片加載成功: pageIndex ${pageIndex}, 尺寸: ${imageObj.width}x${imageObj.height}`);

          // 計算適應畫布的縮放比例
          const stageWidth = this.stage.width();
          const stageHeight = this.stage.height();
          const scale = Math.min(
            (stageWidth - 100) / imageObj.width,
            (stageHeight - 100) / imageObj.height,
            1 // 不放大，最多 1:1
          );

          // 創建 Konva Image 節點
          const konvaImage = new Konva.Image({
            image: imageObj,
            x: 50,
            y: 50,
            width: imageObj.width * scale,
            height: imageObj.height * scale,
            draggable: false, // 題目圖片不可拖曳
            name: 'quizImage'
          });

          // 清空當前 layer 並添加新圖片
          this.layer.destroyChildren();
          this.layer.add(konvaImage);
          this.layer.draw();

          // ✅ 保存到 saveArr（關鍵！）
          this.saveArr[pageIndex] = this.layer.clone();

          resolve(pageIndex);
        } catch (error) {
          console.error(`渲染 Konva 圖片失敗: pageIndex ${pageIndex}`, error);
          reject(error);
        }
      };

      imageObj.onerror = (e) => {
        console.error(`圖片加載失敗: pageIndex ${pageIndex}`, e);
        console.error(`dataURL 前 100 字符:`, imageDataURL.substring(0, 100));
        reject(new Error(`圖片加載失敗: pageIndex ${pageIndex}`));
      };

      // dataURL 不需要設置 crossOrigin（它是內嵌數據，不涉及跨域）
      // 直接設置 src
      imageObj.src = imageDataURL;
    });
  }

  /**
   * 批量渲染多個題目
   * @param {Array} imagesData - 圖片數據數組 [{ dataURL, question }, ...]
   * @param {Number} startPageIndex - 起始頁碼索引
   * @param {Function} progressCallback - 進度回調
   * @returns {Promise<Array>} 返回渲染成功的頁碼數組
   */
  async renderAllQuestions(imagesData, startPageIndex, progressCallback) {
    const renderedPages = [];

    for (let i = 0; i < imagesData.length; i++) {
      // 檢查是否已取消
      if (this.cancelled) {
        console.log(`🛑 渲染已取消，停止在第 ${i + 1}/${imagesData.length} 題`);
        break;
      }

      const imageData = imagesData[i];
      const pageIndex = startPageIndex + i;

      try {
        // 渲染單個題目
        await this.renderSingleQuestion(imageData.dataURL, pageIndex);
        renderedPages.push(pageIndex);

        // 回報進度
        if (progressCallback) {
          progressCallback({
            current: i + 1,
            total: imagesData.length,
            percentage: Math.round(((i + 1) / imagesData.length) * 100)
          });
        }

        // 避免阻塞 UI
        await new Promise(resolve => setTimeout(resolve, 10));
      } catch (error) {
        console.error(`渲染第 ${pageIndex} 頁失敗:`, error);
        // 繼續渲染下一題
      }
    }

    return renderedPages;
  }

  /**
   * 跳轉到指定頁面
   * @param {Number} pageIndex - 頁碼索引
   */
  switchToPage(pageIndex) {
    const savedLayer = this.saveArr[pageIndex];

    if (savedLayer) {
      // 恢復保存的 layer
      this.layer.destroyChildren();
      savedLayer.children.forEach(child => {
        this.layer.add(child.clone());
      });
      this.layer.draw();
      return true;
    }

    return false;
  }
}
